/**
 * Pricing Module - Main Page
 * Tabs: Precificação Direta, Curva ABC, Histórico, Calculadora
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { HelpGuide, HelpAlert, HelpTooltip, HelpSteps } from '../../components/ui/HelpGuide';
import { 
  Calculator, 
  TrendingUp, 
  History, 
  Scale,
  DollarSign,
  Percent,
  Package,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Settings,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Sub-pages
import DirectPricing from './DirectPricing';
import ABCCurvePricing from './ABCCurvePricing';
import HistoricalPricing from './HistoricalPricing';
import ProportionCalculator from './ProportionCalculator';

export default function PricingModule() {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();
  
  const [activeTab, setActiveTab] = useState('direct');
  const [categories, setCategories] = useState([]);
  const [costVariations, setCostVariations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBaseData();
  }, []);

  const loadBaseData = async () => {
    try {
      setLoading(true);
      
      // Load categories
      const catRes = await api.get('/categories');
      setCategories(catRes.data?.items || catRes.data || []);
      
      // Load cost variations
      const varRes = await api.get('/cost-variations');
      setCostVariations(varRes.data?.items || []);
    } catch (err) {
      console.error('Error loading base data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">Carregando módulo de precificação...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="pricing-module">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="h-6 w-6" />
            Módulo de Precificação
          </h1>
          <p className="text-muted-foreground">
            Calcule preços com precisão e garanta sua margem de lucro
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/pricing/variations">
            <Button variant="outline">
              <Settings className="h-4 w-4 mr-2" />
              Variações de Custo
            </Button>
          </Link>
          <Button onClick={loadBaseData} variant="ghost" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Help Guide */}
      <HelpGuide
        title="Como funciona o Módulo de Precificação?"
        description="Este módulo ajuda você a definir preços que garantem sua margem de lucro, considerando todos os custos envolvidos."
        collapsible={true}
        defaultOpen={false}
        className="mb-6"
        sections={[
          {
            title: "Precificação Direta",
            content: "Defina uma margem de lucro e o sistema calcula o preço de venda considerando o custo do produto mais as variações de custo (embalagem, gás, impostos, etc)."
          },
          {
            title: "Curva ABC",
            content: "Classifica produtos em 3 grupos baseado no volume de vendas. Produtos que vendem mais (C) têm margem menor, produtos que vendem menos (A) têm margem maior."
          },
          {
            title: "Histórico de Preços",
            content: "Analisa o histórico de preços para sugerir ajustes baseados em médias, evitando variações bruscas."
          },
          {
            title: "Calculadora de Proporção",
            content: "Calcule quanto custa cada unidade produzida. Ex: Se 1 litro de óleo faz 1000 coxinhas, qual o custo do óleo por coxinha?"
          }
        ]}
        tips={[
          "Configure as variações de custo antes de usar a precificação",
          "Produtos sem preço de custo não podem ser precificados",
          "Revise os preços sugeridos antes de aplicar"
        ]}
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{categories.length}</div>
                <div className="text-xs text-muted-foreground">Categorias</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{costVariations.length}</div>
                <div className="text-xs text-muted-foreground">Variações de Custo</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-2xl font-bold">30%</div>
                <div className="text-xs text-muted-foreground">Margem Média</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-2xl font-bold">-</div>
                <div className="text-xs text-muted-foreground">Último Reajuste</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="direct" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Precificação Direta
          </TabsTrigger>
          <TabsTrigger value="abc" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Curva ABC
          </TabsTrigger>
          <TabsTrigger value="historical" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Histórico
          </TabsTrigger>
          <TabsTrigger value="calculator" className="flex items-center gap-2">
            <Scale className="h-4 w-4" />
            Calculadora
          </TabsTrigger>
        </TabsList>

        <TabsContent value="direct">
          <DirectPricing 
            categories={categories} 
            costVariations={costVariations}
            formatCurrency={formatCurrency}
          />
        </TabsContent>

        <TabsContent value="abc">
          <ABCCurvePricing 
            categories={categories}
            formatCurrency={formatCurrency}
          />
        </TabsContent>

        <TabsContent value="historical">
          <HistoricalPricing 
            categories={categories}
            formatCurrency={formatCurrency}
          />
        </TabsContent>

        <TabsContent value="calculator">
          <ProportionCalculator 
            categories={categories}
            formatCurrency={formatCurrency}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
